/**
 * Google Places (New) — the address search every address input shares.
 *
 * Lifted out of `RffAddressEditingFieldGoogle` when the panel-form sidebars
 * needed a CONTROLLED Google address control (`GoogleAddressInput`). Both use
 * these, so the two can't drift on what an address object looks like — and the
 * address object is a contract: `county` and `coordinateTuple` feed flood
 * determination, the property map and the sizer.
 *
 * Azure Maps is frozen (CLAUDE.md → *Maps*); nothing new should be built on
 * `AddressGeolocateInput`'s Azure search. This is the one to reach for.
 *
 * Cloud Console needs the **Places API (New)** enabled on the project AND
 * ticked in the key's API restrictions — enabling it on the project alone is
 * not enough.
 */
const PLACES_AUTOCOMPLETE_URL =
  "https://places.googleapis.com/v1/places:autocomplete";
const PLACES_DETAILS_URL = "https://places.googleapis.com/v1/places";

const googleMapsKey = () => process.env.REACT_APP_GOOGLE_MAPS_KEY;

const getComponent = (components, type) =>
  components?.find((c) => c.types?.includes(type));

const getShort = (components, type) =>
  getComponent(components, type)?.shortText || "";

const getLong = (components, type) =>
  getComponent(components, type)?.longText || "";

/** A Places detail record → the LOS address object. */
export const mapPlaceDetailsToAddress = (place, suggestionId) => {
  const components = place?.addressComponents || [];
  const lat = place?.location?.latitude || 0;
  const lng = place?.location?.longitude || 0;
  return {
    id: `${place?.id || suggestionId}-${Date.now()}`,
    fullAddress: place?.formattedAddress || "",
    streetNumber: getShort(components, "street_number"),
    streetName: getLong(components, "route"),
    zip: getShort(components, "postal_code"),
    county: getLong(components, "administrative_area_level_2"),
    state: getShort(components, "administrative_area_level_1"),
    city:
      getLong(components, "locality") ||
      getLong(components, "sublocality") ||
      getLong(components, "postal_town"),
    country: getLong(components, "country"),
    countryCode: getShort(components, "country"),
    latitude: lat,
    longitude: lng,
    coordinateTuple: [lat, lng],
    score: 0,
  };
};

/** US street-level suggestions for a typed query. Never throws. */
export const searchPlaces = async (query) => {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(PLACES_AUTOCOMPLETE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": googleMapsKey(),
      },
      body: JSON.stringify({
        input: query,
        includedRegionCodes: ["us"],
        includedPrimaryTypes: [
          "street_address",
          "premise",
          "subpremise",
          "route",
        ],
      }),
    });
    const data = await response.json();
    return (data.suggestions || [])
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .map((p) => ({
        id: p.placeId,
        placeId: p.placeId,
        fullAddress: p.text?.text || "",
        mainText: p.structuredFormat?.mainText?.text || "",
        secondaryText: p.structuredFormat?.secondaryText?.text || "",
      }));
  } catch (err) {
    console.error("Google address search error:", err);
    return [];
  }
};

export const fetchPlaceDetails = async (placeId) => {
  const response = await fetch(`${PLACES_DETAILS_URL}/${placeId}`, {
    method: "GET",
    headers: {
      "X-Goog-Api-Key": googleMapsKey(),
      "X-Goog-FieldMask": "id,formattedAddress,addressComponents,location",
    },
  });
  return response.json();
};

/**
 * Best-effort split of whatever the user typed into the search box, so the
 * manual-entry fields open seeded rather than blank.
 */
export const parseSeedFromQuery = (query) => {
  const seed = {
    streetNumber: "",
    streetName: "",
    city: "",
    state: "",
    zip: "",
  };
  if (!query) return seed;
  // A "unit 6f" / "apt 2" / "#4" segment is the unit, not the city — pull it
  // out (into the model's `addressUnitNumber`) before the positional split.
  const UNIT_RE = /^(?:unit|apt\.?|apartment|suite|ste\.?|#)\s*(.+)$/i;
  const parts = [];
  query
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((p) => {
      const unit = p.match(UNIT_RE);
      if (unit) seed.addressUnitNumber = unit[1].trim();
      else parts.push(p);
    });
  if (parts.length === 0) return seed;
  const street = parts[0];
  const numMatch = street.match(/^(\d+\S*)\s+(.+)$/);
  if (numMatch) {
    seed.streetNumber = numMatch[1];
    seed.streetName = numMatch[2];
  } else {
    seed.streetName = street;
  }
  if (parts[1]) seed.city = parts[1];
  if (parts[2]) {
    const stZip = parts[2].match(/^([A-Za-z]{2})\s*(\d{5}(-\d{4})?)?/);
    if (stZip) {
      seed.state = stZip[1].toUpperCase();
      if (stZip[2]) seed.zip = stZip[2];
    } else if (/^\d{5}/.test(parts[2])) {
      seed.zip = parts[2].slice(0, 5);
    }
  }
  return seed;
};

/** `123 Main St, Unit 6F, Springfield, IL 62704` from the parts. */
export const assembleFullAddress = (a) => {
  if (!a) return "";
  const street = [a.streetNumber, a.streetName].filter(Boolean).join(" ");
  const unit = a.addressUnitNumber ? `Unit ${a.addressUnitNumber}` : "";
  const cityState = [a.city, a.state].filter(Boolean).join(", ");
  const tail = [cityState, a.zip].filter(Boolean).join(" ").trim();
  return [street, unit, tail].filter(Boolean).join(", ");
};

export const isValidZip = (zip) => /^\d{5}(-\d{4})?$/.test(String(zip ?? "").trim());
